import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signin, signup } from '../../api/authApi';
import Cookies from 'js-cookie';
import images from '../../constants/images';
import { jwtDecode } from 'jwt-decode';
import './css/Auth.css';

const Auth = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  
  const [signinForm, setSigninForm] = useState({
    email: '',
    password: ''
  });

  const [signupForm, setSignupForm] = useState({
    phoneNumber: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  // Auto redirect if cookie exists
  useEffect(() => {
    const cookie = Cookies.get('profile');
    if (cookie) {
      try {
        const parsed = JSON.parse(decodeURIComponent(cookie));
        const token = parsed?.token;
        if (token) {
          const decoded = jwtDecode(token);
          const userRole = decoded?.role || 'USER';

          if (decoded.exp * 1000 > Date.now()) {
            if (userRole === 'ADMIN' && location.pathname !== '/admin/dashboard') {
              navigate('/admin/dashboard', { replace: true });
            } else if (userRole === 'USER' && location.pathname !== '/') {
              navigate('/', { replace: true });
            }
          } else {
            Cookies.remove('profile');
          }
        }
      } catch {
        Cookies.remove('profile');
      }
    }
  }, [location.pathname, navigate]);

  const handleSigninSubmit = async (e) => {
    e.preventDefault();

    if (!validateSigninForm()) return;

    setIsLoading(true);
    setError('');

    try {
      const payload = {
        email: signinForm.email,
        password: signinForm.password
      };

      const response = await signin(payload);

      Cookies.set('profile', JSON.stringify(response), {
        expires: rememberMe ? 7 : 1,
        secure: true,
        sameSite: 'Strict',
      });

      const decoded = jwtDecode(response.token);
      const userRole = decoded?.role || 'USER';

      if (userRole === 'ADMIN') {
        navigate('/admin/dashboard', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (err) {
      console.error(err);
      setError('Invalid email or password.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();

    if (!validateSignupForm()) return;

    setIsLoading(true);
    setError('');

    try {
      const payload = {
        phoneNumber: signupForm.phoneNumber,
        email: signupForm.email,
        password: signupForm.password
      };

      const response = await signup(payload);

      Cookies.set('profile', JSON.stringify(response), {
        expires: rememberMe ? 7 : 1,
        secure: true,
        sameSite: 'Strict',
      });

      const decoded = jwtDecode(response.token);
      const userRole = decoded?.role || 'USER';

      if (userRole === 'ADMIN') {
        navigate('/admin/dashboard', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (err) {
      console.error(err);
      setError('Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSigninInputChange = (e) => {
    const { name, value } = e.target;
    setSigninForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSignupInputChange = (e) => {
    const { name, value } = e.target;
  
    setSignupForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateSigninForm = () => {
    const newErrors = {};

    if (!signinForm.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(signinForm.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!signinForm.password) {
      newErrors.password = 'Password is required';
    } else if (signinForm.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateSignupForm = () => {
    const newErrors = {};

    if (!signupForm.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^\+?[\d\s\-()]{10,}$/.test(signupForm.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }

    if (!signupForm.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(signupForm.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!signupForm.password) {
      newErrors.password = 'Password is required';
    } else if (signupForm.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!signupForm.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (signupForm.password !== signupForm.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const toggleAuthMode = () => {
    setIsSignUp(!isSignUp);
    setErrors({});
    setError('');
    setSigninForm({ email: '', password: '' });
    setSignupForm({
      phoneNumber: '',
      email: '',
      password: '',
      confirmPassword: ''
    });
  };

  return (
    <div className="auth-container">
      <div className="auth-background">
        <div className="auth-overlay"></div>
      </div>

      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo">
            <img src={images.pho1986} alt="logo" className="logo-icon" />
            <h1>Welcome</h1>
          </div>
          <p className="auth-subtitle">
            {isSignUp ? 'Create your account' : 'Sign in to your account'}
          </p>
        </div>

        {!isSignUp ? (
          // Sign In Form
          <form className="auth-form" onSubmit={handleSigninSubmit}>
            {/* Email */}
            <div className="sign-form-group">
              <label htmlFor="email" className="sign-form-label">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={signinForm.email}
                onChange={handleSigninInputChange}
                className={`form-input ${errors.email ? 'error' : ''}`}
                placeholder="Enter your email"
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            {/* Password */}
            <div className="sign-form-group password-group">
              <label htmlFor="password" className="sign-form-label">
                Password
              </label>
              <div className="password-wrapper">
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={signinForm.password}
                  onChange={handleSigninInputChange}
                  className={`form-input ${errors.password ? 'error' : ''}`}
                  placeholder="Enter your password"
                />
              </div>
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>

            <div className="form-options">
              <label className="checkbox-container">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span className="checkmark"></span>
                Remember me
              </label>
            </div>

            {error && <div className="error-global">{error}</div>}

            <button
              type="submit"
              className={`auth-button ${isLoading ? 'loading' : ''}`}
              disabled={isLoading}
            >
              {isLoading ? <div className="loading-spinner"></div> : 'Sign In'}
            </button>
          </form>
        ) : (
          // Sign Up Form
          <form className="auth-form" onSubmit={handleSignupSubmit}>

            {/* Phone Number */}
            <div className="sign-form-group">
              <label htmlFor="phoneNumber" className="sign-form-label">
                Phone Number
              </label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={signupForm.phoneNumber}
                onChange={handleSignupInputChange}
                className={`form-input ${errors.phoneNumber ? 'error' : ''}`}
                placeholder="Enter your phone number"
              />
              {errors.phoneNumber && <span className="error-message">{errors.phoneNumber}</span>}
            </div>

            {/* Email */}
            <div className="sign-form-group">
              <label htmlFor="email" className="sign-form-label">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={signupForm.email}
                onChange={handleSignupInputChange}
                className={`form-input ${errors.email ? 'error' : ''}`}
                placeholder="Enter your email"
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            {/* Password */}
            <div className="sign-form-group password-group">
              <label htmlFor="password" className="sign-form-label">
                Password
              </label>
              <div className="password-wrapper">
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={signupForm.password}
                  onChange={handleSignupInputChange}
                  className={`form-input ${errors.password ? 'error' : ''}`}
                  placeholder="Enter your password"
                />
              </div>
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>

            {/* Confirm Password */}
            <div className="sign-form-group password-group">
              <label htmlFor="confirmPassword" className="sign-form-label">
                Confirm Password
              </label>
              <div className="password-wrapper">
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={signupForm.confirmPassword}
                  onChange={handleSignupInputChange}
                  className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                  placeholder="Confirm your password"
                />
              </div>
              {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
            </div>

            <div className="form-options">
              <label className="checkbox-container">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span className="checkmark"></span>
                Remember me
              </label>
            </div>

            {error && <div className="error-global">{error}</div>}

            <button
              type="submit"
              className={`auth-button ${isLoading ? 'loading' : ''}`}
              disabled={isLoading}
            >
              {isLoading ? <div className="loading-spinner"></div> : 'Sign Up'}
            </button>
          </form>
        )}

        {/* Toggle between Sign In and Sign Up */}
        <div className="auth-toggle">
          <p>
            {isSignUp ? 'Already have an account?' : "Don't have an account?"}
            <button
              type="button"
              className="toggle-button"
              onClick={toggleAuthMode}
            >
              {isSignUp ? 'Sign In' : 'Sign Up'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;